import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  FaMapMarkerAlt,
  FaTaxi,
  FaCalendarAlt,
  FaRupeeSign,
  FaEye,
  FaSearch,
} from "react-icons/fa";
import {
  getPaymentMethodDisplay,
  getPaymentStatusDisplay,
  getPaymentStatusBadgeClass,
} from "../utils/paymentDisplayUtils";
import { convertTo12HourFormat } from "../utils/timeUtils";

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const itemsPerPage = 10; // 10 bookings per page

  // Get user authentication data from Redux store instead of localStorage
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    // Import API URL from config
    const { API_URL } = require("../config/apiConfig");

    // Get bookings from MongoDB database only using API
    const fetchBookings = async () => {
      setIsLoading(true);

      try {
        // Get authentication token from Redux store
        let config = {};

        if (user && user.token) {
          config = {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          };
        }

        // Fetch user-specific bookings from MongoDB through API
        // Use the same endpoint as Dashboard for consistency
        const apiUrl = `${API_URL}/booking/my-bookings`;
        const response = await axios.get(apiUrl, config);

        if (response.data && response.data.success) {

          // Calculate pagination on frontend since my-bookings returns all user bookings
          const totalUserBookings = response.data.data.length;
          const calculatedTotalPages = Math.ceil(totalUserBookings / itemsPerPage);
          setTotalBookings(totalUserBookings);
          setTotalPages(calculatedTotalPages);
          setHasNextPage(currentPage < calculatedTotalPages);
          setHasPrevPage(currentPage > 1);

          // Transform API data to the format needed by the component
          const formattedBookings = response.data.data.map((booking) => {
            // Extract data depending on whether fields are populated objects or strings
            const pickupLocation =
              typeof booking.pickupLocation === "object" &&
              booking.pickupLocation?.name
                ? `${booking.pickupLocation.name}${booking.pickupLocation.state ? `, ${booking.pickupLocation.state}` : ""}`
                : booking.pickupAddress || booking.from || "";

            const dropLocation =
              typeof booking.dropLocation === "object" &&
              booking.dropLocation?.name
                ? `${booking.dropLocation.name}${booking.dropLocation.state ? `, ${booking.dropLocation.state}` : ""}`
                : booking.dropAddress || booking.to || "";

            const cabName =
              typeof booking.cabType === "object" && booking.cabType?.name
                ? booking.cabType.name
                : "";

            // Extract driver information if available
            let driverName = "Not assigned";
            let driverContact = "N/A";
            let driverInfo = null;

            if (booking.driver && booking.status === "assigned") {
              if (
                typeof booking.driver === "object" &&
                booking.driver !== null
              ) {
                driverName =
                  `${booking.driver.firstName || ""} ${booking.driver.lastName || ""}`.trim() ||
                  "Driver";
                driverContact = booking.driver.phone || "Not provided";
                driverInfo = booking.driver;
              } else if (typeof booking.driver === "string" && booking.driver) {
                driverName = "Driver Assigned";
                driverContact = "See details";
              }
            }

            // Format booking ID - use bookingId if available or _id as fallback
            const bookingId = booking.bookingId || booking._id;

            // Normalize payment method - handle various backend values
            let normalizedPaymentMethod = "online"; // default to online
            let normalizedPaymentStatus = booking.paymentStatus || "pending";


            if (booking.paymentMethod) {
              const method = booking.paymentMethod.toLowerCase();
              if (
                method === "cod" ||
                method === "cash" ||
                method === "cash on delivery"
              ) {
                normalizedPaymentMethod = "cod";
              } else {
                // Everything else is considered online payment
                normalizedPaymentMethod = "online";
                // For online payments, if status is not explicitly paid, set as paid
                if (
                  normalizedPaymentStatus !== "paid" &&
                  normalizedPaymentStatus !== "completed"
                ) {
                  normalizedPaymentStatus = "paid";
                }
              }
            }


            return {
              id: booking._id,
              bookingId: bookingId,
              from: pickupLocation,
              to: dropLocation,
              date: booking.pickupDate,
              time:
                booking.pickupTime ||
                booking.travelTime ||
                "Time not specified",
              status: booking.status,
              amount: booking.totalAmount,
              cabName: cabName,
              paymentMethod: normalizedPaymentMethod,
              paymentStatus: normalizedPaymentStatus,
              userName: booking.passengerDetails?.name,
              userEmail: booking.passengerDetails?.email,
              userPhone: booking.passengerDetails?.phone,
              driverName: driverName,
              driverContact: driverContact,
              driverInfo: driverInfo,
              assignedAt: booking.assignedAt,

              // Service type information (improved detection)
              serviceType: (() => {
                // First check explicit serviceType from backend - this is most reliable
                if (booking.serviceType && booking.serviceType !== "undefined") {
                  return booking.serviceType;
                }

                // Strong indicators for airport service
                if (booking.airport || booking.airportCode || booking.transferDirection) {
                  return "airport";
                }

                // Strong indicators for rental service
                if (booking.packageName || booking.rentalHours || booking.rentalKm) {
                  return "rental";
                }

                // Extract and clean location strings for comparison
                const extractLocationName = (location) => {
                  if (!location) return "";
                  if (typeof location === "object" && location?.name) {
                    return location.name.toLowerCase().trim();
                  }
                  if (typeof location === "string") {
                    // Skip ObjectIds (24 character hex strings)
                    if (location.length === 24 && /^[0-9a-fA-F]{24}$/.test(location)) {
                      return "";
                    }
                    return location.toLowerCase().trim();
                  }
                  return "";
                };

                const pickupName = extractLocationName(booking.pickupLocation) ||
                                 extractLocationName(booking.from) ||
                                 (booking.pickupAddress ? booking.pickupAddress.toLowerCase().trim() : "");
                const dropName = extractLocationName(booking.dropLocation) ||
                               extractLocationName(booking.to) ||
                               (booking.dropAddress ? booking.dropAddress.toLowerCase().trim() : "");

                // Only consider it rental if both locations are exactly the same AND non-empty
                // AND there are clear rental indicators
                if (pickupName && dropName && pickupName === dropName &&
                    (booking.packageName || booking.rentalHours || booking.rentalKm)) {
                  return "rental";
                }

                // For cases like "Porbandar to Dwarka" - these are clearly different cities = Outstation
                if (pickupName && dropName && pickupName !== dropName) {
                  return "outstation";
                }

                // If we can't determine locations clearly, default to outstation
                return "outstation";
              })(),

              // Trip type information (One Way vs Round Trip vs Multi-City)
              tripType: (() => {
                // Check if booking has explicit journey type (primary field)
                if (booking.journeyType && booking.journeyType !== "undefined") {
                  if (booking.journeyType === "roundTrip") {
                    return "roundTrip";
                  }
                  if (booking.journeyType === "oneWay") {
                    return "oneWay";
                  }
                  // Support legacy formats if any exist
                  if (booking.journeyType === "round_trip" || booking.journeyType === "round-trip") {
                    return "roundTrip";
                  }
                  if (booking.journeyType === "one_way" || booking.journeyType === "one-way") {
                    return "oneWay";
                  }
                }

                // Check if booking has explicit trip type (fallback)
                if (booking.tripType && booking.tripType !== "undefined") {
                  return booking.tripType;
                }

                // Check for round trip indicators
                if (booking.returnDate || booking.returnTime) {
                  return "roundTrip";
                }

                // Check for return date as string (not null/empty)
                if (booking.returnDate && booking.returnDate !== "null" && booking.returnDate !== "") {
                  return "roundTrip";
                }

                // Check for via cities (multi-city trip)
                if (booking.viaCities && booking.viaCities.length > 0) {
                  return "multiCity";
                }

                // Default to one way
                return "oneWay";
              })(),

              // Via cities information - check both database and potential URL params
              viaCities: (() => {
                // First check if booking has via cities
                if (booking.viaCities && Array.isArray(booking.viaCities) && booking.viaCities.length > 0) {
                  return booking.viaCities;
                }

                // Check if there's via cities data in any route parameters stored in the booking
                if (booking.routeData && booking.routeData.viaCities) {
                  return booking.routeData.viaCities;
                }

                // Check if via cities are stored as a string and need to be parsed
                if (booking.viaCitiesString) {
                  return booking.viaCitiesString.split(',').map(city => city.trim()).filter(city => city.length > 0);
                }

                return [];
              })(),

              // Rental service information
              packageName: booking.packageName,
              rentalHours: booking.rentalHours,
              rentalKm: booking.rentalKm,

              rawData: booking, // Keep the original data for debugging
            };
          });

          setBookings(formattedBookings);
        } else {
          console.warn("No bookings found in the database");
          setBookings([]);
          setTotalBookings(0);
          setTotalPages(1);
        }
      } catch (error) {
        console.error("Error fetching bookings from API:", error);
        alert(
          "Failed to load bookings from the database. Please try again later.",
        );
        setBookings([]);
        setTotalBookings(0);
        setTotalPages(1);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookings();
  }, []); // Fetch all user bookings once

  // Note: Filtering is now applied to current page data in filteredCurrentBookings above

  // Apply search and filter to all bookings, then paginate
  const normalizedSearch = searchTerm.trim().toLowerCase().replace(/^#/, "");
  const filteredBookings = bookings.filter((booking) => {
    const bookingIdStr = String(booking.bookingId || "").toLowerCase();
    const internalIdStr = String(booking.id || "").toLowerCase();

    const matchesSearch =
      (booking.from && booking.from.toLowerCase().includes(normalizedSearch)) ||
      (booking.to && booking.to.toLowerCase().includes(normalizedSearch)) ||
      (bookingIdStr && bookingIdStr.includes(normalizedSearch)) ||
      (internalIdStr && internalIdStr.includes(normalizedSearch)) ||
      (booking.cabName && booking.cabName.toLowerCase().includes(normalizedSearch));

    if (filter === "all") return matchesSearch;
    if (filter === "upcoming") {
      const bookingDate = new Date(booking.date);
      const today = new Date();
      return matchesSearch && bookingDate >= today;
    }
    if (filter === "completed") {
      const bookingDate = new Date(booking.date);
      const today = new Date();
      return matchesSearch && bookingDate < today;
    }

    return matchesSearch;
  });

  // Client-side pagination
  const filteredTotalPages = Math.max(1, Math.ceil(filteredBookings.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBookings = filteredBookings.slice(startIndex, endIndex);

  // Reset to first page when filter or search changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchTerm, filter]);

  // Pagination handlers (based on filtered results)
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, filteredTotalPages));
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 flex justify-center items-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">My Bookings</h1>
            <p className="text-gray-600 mt-1">
              View and manage all your cab bookings
            </p>
          </div>
          <Link
            to="/"
            className="mt-4 md:mt-0 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Book a New Cab
          </Link>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search bookings..."
                className="pl-10 w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex-shrink-0">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Bookings</option>
                <option value="upcoming">Upcoming</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Bookings List */}
        {currentBookings.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <div className="rounded-full bg-blue-100 p-3 inline-block mb-4">
              <FaTaxi className="h-8 w-8 text-blue-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              No Bookings Found
            </h2>
            <p className="text-gray-600 mb-6">
              You haven't made any bookings yet or no bookings match your
              search.
            </p>
            <Link
              to="/"
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Book Your First Cab
            </Link>
          </div>
        ) : (
          <>
            {/* Pagination Info */}
            <div className="mb-4 text-sm text-gray-600">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredBookings.length)} of {filteredBookings.length} bookings
              {Math.ceil(filteredBookings.length / itemsPerPage) > 1 && ` (Page ${currentPage} of ${Math.ceil(filteredBookings.length / itemsPerPage)})`}
            </div>

            <div className="grid grid-cols-1 gap-4">
              {currentBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="bg-white rounded-xl shadow-md overflow-hidden transition-all hover:shadow-lg"
                >
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row justify-between">
                      <div className="mb-4 md:mb-0">
                        <div className="flex items-start mb-4">
                          <div className="flex-shrink-0 bg-blue-100 p-2 rounded-full mr-4">
                            <FaMapMarkerAlt className="text-blue-600" />
                          </div>
                          <div>
                            {/* Check if this is a rental/local service */}
                            {booking.serviceType === "rental" ? (
                              <div>
                                <h3 className="font-semibold text-lg text-gray-800">
                                  📦 {booking.packageName ||
                                      (booking.rentalHours && booking.rentalKm
                                        ? `${booking.rentalHours} Hour / ${booking.rentalKm} Km`
                                        : "Rental Service")}
                                </h3>
                                <div className="text-sm text-gray-600 mt-1 space-y-1">
                                  {booking.rentalHours && (
                                    <div>
                                      ⏰ Duration: {booking.rentalHours} hours
                                    </div>
                                  )}
                                  {booking.rentalKm && (
                                    <div>
                                      📏 Included Distance: {booking.rentalKm} km
                                    </div>
                                  )}
                                </div>
                                {/* Show detailed pickup address for rental services */}
                                <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                                  <span className="text-xs font-medium text-blue-800">Pickup Location Details:</span>
                                  <div className="text-sm text-blue-700 mt-1 font-medium">
                                    {booking.from}
                                  </div>
                                  <div className="text-xs font-medium text-blue-800 mt-2">Address:</div>
                                  <div className="text-sm text-blue-700 mt-1">
                                    {booking.rawData?.pickupAddress ||
                                     booking.rawData?.from ||
                                     "Rajkot West Taluka, राजकोट, Gujarat, 360007, India"}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <h3 className="font-semibold text-lg text-gray-800">
                                    {booking.from} to {booking.to}
                                  </h3>
                                  {/* Show trip type indicator for outstation bookings */}
                                  {booking.serviceType === "outstation" && (
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                      booking.tripType === "roundTrip"
                                        ? "bg-purple-100 text-purple-800"
                                        : booking.viaCities && booking.viaCities.length > 0
                                        ? "bg-green-100 text-green-800"
                                        : "bg-blue-100 text-blue-800"
                                    }`}>
                                      {(() => {
                                        if (booking.tripType === "roundTrip") {
                                          return "Round Trip";
                                        } else if (booking.tripType === "multiCity" || (booking.viaCities && booking.viaCities.length > 0)) {
                                          const viaCities = booking.viaCities || [];
                                          return viaCities.length > 0
                                            ? `Multi-City (via ${viaCities.join(", ")})`
                                            : "Multi-City";
                                        } else {
                                          return "One Way";
                                        }
                                      })()}
                                    </span>
                                  )}
                                </div>
                                {/* Show detailed addresses for outstation bookings */}
                                <div className="mt-2 space-y-1">
                                  {/* Pickup Address Details */}
                                  <div className="p-2 bg-green-50 rounded border border-green-200">
                                    <div className="text-xs font-medium text-green-800">Pickup Location Details:</div>
                                    <div className="text-sm text-green-700 mt-1 font-medium">
                                      {booking.from}
                                    </div>
                                    <div className="text-xs font-medium text-green-800 mt-1">Address:</div>
                                    <div className="text-xs text-green-700 mt-1">
                                      {booking.rawData?.pickupAddress ||
                                       (typeof booking.rawData?.pickupLocation === 'object' ?
                                        booking.rawData?.pickupLocation?.address ||
                                        booking.rawData?.pickupLocation?.name ||
                                        'Address details not available' :
                                        booking.rawData?.pickupLocation ||
                                        'Address details not available')}
                                    </div>
                                  </div>

                                  {/* Drop Address Details */}
                                  <div className="p-2 bg-red-50 rounded border border-red-200">
                                    <div className="text-xs font-medium text-red-800">Drop Location Details:</div>
                                    <div className="text-sm text-red-700 mt-1 font-medium">
                                      {booking.to}
                                    </div>
                                    <div className="text-xs font-medium text-red-800 mt-1">Address:</div>
                                    <div className="text-xs text-red-700 mt-1">
                                      {booking.rawData?.dropAddress ||
                                       (typeof booking.rawData?.dropLocation === 'object' ?
                                        booking.rawData?.dropLocation?.address ||
                                        booking.rawData?.dropLocation?.name ||
                                        'Address details not available' :
                                        booking.rawData?.dropLocation ||
                                        'Address details not available')}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            <p className="text-sm text-gray-500">
                              Booking ID:{" "}
                              <span className="font-medium">
                                {booking.bookingId || booking.id}
                              </span>
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-4">
                          <div className="flex items-center">
                            <FaCalendarAlt className="text-gray-500 mr-2" />
                            <span className="text-sm">
                              {booking.date
                                ? new Date(booking.date).toLocaleDateString(
                                    "en-GB",
                                    {
                                      day: "numeric",
                                      month: "long",
                                      year: "numeric",
                                    },
                                  )
                                : "Not specified"}
                            </span>
                          </div>
                          <div className="flex items-center">
                            <span className="text-gray-500 mr-2">🕒</span>
                            <span className="text-sm">
                              {convertTo12HourFormat(booking.time) || "Time not specified"}
                            </span>
                          </div>
                          <div className="flex items-center">
                            <FaTaxi className="text-gray-500 mr-2" />
                            <span className="text-sm">
                              {booking.cabName || "Not specified"}
                            </span>
                          </div>
                          <div className="flex items-center">
                            <FaRupeeSign className="text-gray-500 mr-2" />
                            <span className="text-sm">
                              ₹{booking.amount || 0}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col space-y-2">
                        {/* Payment Method Display */}
                        <div className="px-3 py-1 rounded-full text-sm font-medium text-center bg-blue-100 text-blue-800">
                          {getPaymentMethodDisplay(booking.paymentMethod)}
                        </div>

                        {/* Payment Status Display */}
                        <div
                          className={`px-3 py-1 rounded-full text-sm font-medium text-center ${
                            getPaymentStatusBadgeClass(booking)
                          }`}
                        >
                          {getPaymentStatusDisplay(booking)}
                        </div>

                        {/* Driver information if assigned */}
                        {booking.status === "assigned" &&
                          booking.driverName !== "Not assigned" && (
                            <div className="mt-2 px-3 py-1 rounded-full text-sm font-medium bg-green-50 text-green-700">
                              Driver: {booking.driverName}
                            </div>
                          )}
                        <Link
                          to={`/booking/${booking.bookingId || booking.id}`}
                          className="mt-2 flex items-center justify-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                        >
                          <FaEye /> View Details
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination Controls */}
        {(() => {
          const filteredTotalPages = Math.ceil(filteredBookings.length / itemsPerPage);
          return filteredTotalPages > 1 && (
            <div className="mt-8 flex justify-center">
              <nav className="flex items-center space-x-2">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded border ${
                    currentPage === 1
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>

                {/* Page numbers */}
                {Array.from({ length: Math.min(filteredTotalPages, 10) }, (_, i) => {
                  let pageNumber;
                  if (filteredTotalPages <= 10) {
                    pageNumber = i + 1;
                  } else {
                    // Show pages around current page
                    const start = Math.max(1, currentPage - 4);
                    const end = Math.min(filteredTotalPages, start + 9);
                    pageNumber = start + i;
                    if (pageNumber > end) return null;
                  }

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageClick(pageNumber)}
                      className={`px-3 py-1 rounded ${
                        currentPage === pageNumber
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 text-gray-700 hover:bg-blue-50'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                }).filter(Boolean)}

                <button
                  onClick={handleNextPage}
                  disabled={currentPage === filteredTotalPages}
                  className={`px-3 py-1 rounded border ${
                    currentPage === filteredTotalPages
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default Bookings;
